import { MutationStatus } from 'react-query';
import { assign, ErrorExecutionEvent, send } from 'xstate';

import { showErrorToast } from '@nw/shared/ui';
import {
  getAPIErrorEnum,
  getAPIErrorMessage,
  getAPIErrorResponse,
  getQueryParamValue
} from '@nw/shared/utils';

import {
  createProductDetailModel,
  createProductWithUpdatedPricingDetailModel
} from '../../factories/ProductDetailFactory';
import PaymentGateways from '../../models/PaymentGateways';
import PaymentOrderDetailsModel from '../../models/PaymentOrderDetailsModel';
import { userOrdersDetailsTransformer } from '../../transformers/UserOrdersTransformer';
import {
  CheckReferralCodeEvent,
  CheckReferralCodeSuccessEvent,
  CreateChangeProgramOrderSuccessEvent,
  CreatePaymentOrderAndGetDetailSuccessEvent,
  CreatePaymentOrderEvent,
  GetProductDetailsSuccessEvent,
  GetProductsPurchaseDetailsSuccessEvent,
  GetProductsPurchaseEvent,
  GetReferralCodesSuccessEvent,
  GetSelectedProductPaymentServiceSuccessEvent,
  GetUserOrderDetailsSuccessEvent,
  OnClickPayButtonEvent,
  PaymentCoreMachineEvents,
  PaymentsCoreMachineContext,
  SetShouldShowProductFieldsEvent,
  UpdateIsChangedProgramProductEvent
} from '../../types/paymentCoreMachine.types';
import { AllProductTypes } from '../../types/storesTypes';

const showErrorMessageToast = (message: string) => {
  showErrorToast(message, {}, null, {
    fontFamily: 'Inter',
    borderRadius: '12px',
    height: '100px'
  });
};

export const setPaymentCoreMachineApiStatus = (
  context: PaymentsCoreMachineContext,
  apiStatus: string,
  apiStatusValue: MutationStatus = 'loading'
) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      [apiStatus]: apiStatusValue
    }
  };
};

export const onEnterGetUserOrdersDetailsWithFiltersState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      getUserOrdersDetailsWithFiltersApiStatus: 'loading'
    }
  };
});

export const onSuccessGetUserOrdersDetailsWithFilters = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as GetUserOrderDetailsSuccessEvent;

  // Transform the response using the transformer
  const userOrdersDetailsModel = userOrdersDetailsTransformer(typedEvent.data);

  const { productCodes, productsDetailedInfo } = context;

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  for (const productCode of productCodes ?? []) {
    const productOrderDetails =
      userOrdersDetailsModel.getOrderByProductCode(productCode);
    if (productOrderDetails) {
      updatedProductsDetailedInfo[productCode] = {
        productUserOrdersDetails: productOrderDetails
      };
    }
  }

  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    userOrdersDetails: userOrdersDetailsModel,
    apiStatus: {
      ...context.apiStatus,
      getUserOrdersDetailsWithFiltersApiStatus: 'success'
    }
  };
});

export const onErrorGetUserOrdersDetailsWithFilters = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  return {
    apiStatus: {
      ...context.apiStatus,
      getUserOrdersDetailsWithFiltersApiStatus: 'error'
    },
    errorMessages: {
      ...context.errorMessages,
      getUserOrdersDetailsWithFiltersErrorMessage: errorMessage
    }
  };
});

export const onEnterGetProductsPurchaseState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as GetProductsPurchaseEvent;

  return {
    productCodes: typedEvent.productCodes,
    apiStatus: {
      ...context.apiStatus,
      getProductsPurchaseApiStatus: 'loading'
    }
  };
});

export const onSuccessGetProductsPurchase = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as GetProductsPurchaseDetailsSuccessEvent;
  const { productsDetailedInfo } = context;

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  // Create product detail models for each product
  if (typedEvent.data?.products_details) {
    typedEvent.data.products_details.forEach((productDetail) => {
      const productCode = productDetail.product_code;

      // Create enhanced product detail model
      const productDetailModel = createProductDetailModel(
        productDetail,
        context.userProfileDetails
      );

      // Update productsDetailedInfo with enhanced model
      updatedProductsDetailedInfo[productCode] = {
        ...updatedProductsDetailedInfo[productCode],
        productDetails: productDetailModel
      };
    });
  }

  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    apiStatus: {
      ...context.apiStatus,
      getProductsPurchaseApiStatus: 'success'
    }
  };
});

export const onErrorGetProductsPurchase = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  return {
    apiStatus: {
      ...context.apiStatus,
      getProductsPurchaseApiStatus: 'error'
    },
    errorMessages: {
      ...context.errorMessages,
      getProductsPurchaseErrorMessage: errorMessage
    }
  };
});

export const sendMachineToGetSelectedProductReferralCodes = send<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    type: 'GET_SELECTED_PRODUCT_REFERRAL_CODES'
  };
});

export const setShouldShowProductFields = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as SetShouldShowProductFieldsEvent;
  const { allProductRoutes } = context;
  const selectedProductRoute = allProductRoutes.find(
    (productRoute) => productRoute.product_code === typedEvent.productCode
  );
  if (typedEvent.shouldShowProductFields) {
    return {
      shouldShowProductFields: typedEvent.shouldShowProductFields,
      selectedProductCode: typedEvent.productCode,
      selectedProductRouteDetails: selectedProductRoute
    };
  } else {
    return {
      shouldShowProductFields: typedEvent.shouldShowProductFields,
      selectedProductCode: null,
      userSelectedPaymentService: null,
      formValues: {},
      promotionalCodes: null,
      isCouponCodeApplied: false,
      isInviteCodeApplied: false,
      isChangedProgramProduct: false,
      checkReferralCodeEvent: null,
      apiStatus: {
        ...context.apiStatus,
        getProductDetailsApiStatus: 'idle',
        getReferralCodesApiStatus: 'idle',
        getSelectedProductPaymentServiceApiStatus: 'idle',
        createPaymentOrderAndGetDetailApiStatus: 'idle',
        createInstallmentOrderApiStatus: 'idle',
        cancelInstallmentOrderApiStatus: 'idle',
        getOrderStatusApiStatus: 'idle',
        updateUserProfileDetailsApiStatus: 'idle',
        checkCouponCodeApiStatus: 'idle',
        checkInviteCodeApiStatus: 'idle',
        createChangeProgramOrderApiStatus: 'idle'
      },
      errorMessages: {
        ...context.errorMessages,
        getProductDetailsErrorMessage: '',
        getReferralCodesErrorMessage: '',
        getSelectedProductPaymentServiceErrorMessage: '',
        createPaymentOrderAndGetDetailErrorMessage: '',
        createInstallmentErrorMessage: '',
        cancelInstallmentErrorMessage: '',
        getOrderStatusErrorMessage: '',
        updateUserProfileDetailsErrorMessage: '',
        checkCouponCodeErrorMessage: '',
        checkInviteCodeErrorMessage: '',
        createChangeProgramOrderErrorMessage: ''
      }
    };
  }
});

export const onEnterGetProductDetailsState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      getProductDetailsApiStatus: 'loading'
    }
  };
});

export const onSuccessGetProductDetails = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as GetProductDetailsSuccessEvent;

  const { productsDetailedInfo } = context;

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  const productCode = typedEvent.data.product_code;

  updatedProductsDetailedInfo[productCode] = {
    ...updatedProductsDetailedInfo[productCode],
    productDetails: createProductDetailModel(
      typedEvent.data,
      context.userProfileDetails
    )
  };

  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    apiStatus: {
      ...context.apiStatus,
      getProductDetailsApiStatus: 'success'
    }
  };
});

export const onErrorGetProductDetails = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  return {
    apiStatus: {
      ...context.apiStatus,
      getProductDetailsApiStatus: 'error'
    },
    errorMessages: {
      ...context.errorMessages,
      getProductDetailsErrorMessage: errorMessage
    }
  };
});

export const onEnterGetReferralCodesState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      getReferralCodesApiStatus: 'loading'
    }
  };
});

export const onSuccessGetReferralCodes = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as GetReferralCodesSuccessEvent;
  const inviteCode = typedEvent.data.invite_code;

  return {
    promotionalCodes: {
      ...context.promotionalCodes,
      invite_code: inviteCode ?? getQueryParamValue('invite_code') ?? '',
      coupon_code: getQueryParamValue('coupon_code') ?? ''
    },
    isInviteCodeApplied: inviteCode ? true : context.isInviteCodeApplied,
    isInviteCodeAutoApplied: inviteCode
      ? true
      : context.isInviteCodeAutoApplied,
    apiStatus: {
      ...context.apiStatus,
      getReferralCodesApiStatus: 'success',
      checkInviteCodeApiStatus: inviteCode
        ? 'success'
        : context.apiStatus.checkInviteCodeApiStatus
    }
  };
});

export const onErrorGetReferralCodes = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  return {
    apiStatus: {
      ...context.apiStatus,
      getReferralCodesApiStatus: 'error'
    },
    errorMessages: {
      ...context.errorMessages,
      getReferralCodesErrorMessage: errorMessage
    }
  };
});

export const updateFormValues = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as OnClickPayButtonEvent;
  return {
    formValues: {
      ...context.formValues,
      ...typedEvent.values
    }
  };
});

export const onEnterGetSelectedProductPaymentServiceState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      getSelectedProductPaymentServiceApiStatus: 'loading'
    }
  };
});

export const onSuccessGetSelectedProductPaymentService = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as GetSelectedProductPaymentServiceSuccessEvent;

  const { productsDetailedInfo, selectedProductCode } = context;

  if (
    !selectedProductCode ||
    !productsDetailedInfo ||
    !productsDetailedInfo[selectedProductCode]
  ) {
    return {};
  }

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  updatedProductsDetailedInfo[selectedProductCode] = {
    ...updatedProductsDetailedInfo[selectedProductCode],
    paymentServiceInfo: new PaymentGateways(typedEvent.data)
  };

  const paymentServiceInfo =
    updatedProductsDetailedInfo[selectedProductCode].paymentServiceInfo;

  if (!paymentServiceInfo) {
    return {};
  }

  if (
    paymentServiceInfo.paymentServicesDetails.length > 1 ||
    paymentServiceInfo.paymentServicesDetails[0].paymentModes.length > 1
  ) {
    updatedProductsDetailedInfo[
      selectedProductCode
    ].shouldShowPaymentModeOptions = true;
  }

  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    apiStatus: {
      ...context.apiStatus,
      getSelectedProductPaymentServiceApiStatus: 'success'
    }
  };
});

export const onErrorGetSelectedProductPaymentService = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  showErrorMessageToast(errorMessage);
  return {
    apiStatus: {
      ...context.apiStatus,
      getSelectedProductPaymentServiceApiStatus: 'error'
    }
  };
});

export const onEnterCreatePaymentOrderAndGetDetailState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      createPaymentOrderAndGetDetailApiStatus: 'loading'
    }
  };
});

export const onSuccessCreatePaymentOrderAndGetDetail = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as CreatePaymentOrderAndGetDetailSuccessEvent;

  const { productsDetailedInfo, selectedProductCode } = context;

  if (!selectedProductCode || !productsDetailedInfo?.[selectedProductCode]) {
    return {};
  }

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  updatedProductsDetailedInfo[selectedProductCode] = {
    ...updatedProductsDetailedInfo[selectedProductCode],
    createPaymentOrderResponse: new PaymentOrderDetailsModel(typedEvent.data)
  };

  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    apiStatus: {
      ...context.apiStatus,
      createPaymentOrderAndGetDetailApiStatus: 'success'
    }
  };
});

export const onErrorCreatePaymentOrderAndGetDetail = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorResponse(typedEvent.data);

  const errorEnum = getAPIErrorEnum(typedEvent.data);

  if (errorEnum === 'PAYMENT_ALREADY_CREATED_FOR_ORDER') {
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  }

  showErrorMessageToast(errorMessage);
  return {
    apiStatus: {
      ...context.apiStatus,
      createPaymentOrderAndGetDetailApiStatus: 'error'
    }
  };
});

export const onEnterGetOrderStatusState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      checkOrderStatusApiStatus: 'loading'
    }
  };
});

export const onSuccessGetOrderStatus = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  window.location.reload();
  return {
    apiStatus: {
      ...context.apiStatus,
      checkOrderStatusApiStatus: 'success'
    }
  };
});

export const onErrorGetOrderStatus = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  showErrorMessageToast(errorMessage);
  return {
    apiStatus: {
      ...context.apiStatus,
      checkOrderStatusApiStatus: 'error'
    }
  };
});

export const onEnterCreateInstallmentOrderState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      createInstallmentOrderApiStatus: 'loading'
    }
  };
});

export const onSuccessCreateInstallmentOrder = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      createInstallmentOrderApiStatus: 'success'
    }
  };
});

export const onErrorCreateInstallmentOrder = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  showErrorMessageToast(errorMessage);
  return {
    apiStatus: {
      ...context.apiStatus,
      createInstallmentOrderApiStatus: 'error'
    }
  };
});

export const onEnterCancelInstallmentOrderState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      cancelInstallmentOrderApiStatus: 'loading'
    }
  };
});

export const onSuccessCancelInstallmentOrder = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      cancelInstallmentOrderApiStatus: 'success'
    }
  };
});

export const onErrorCancelInstallmentOrder = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  showErrorMessageToast(errorMessage);
  return {
    apiStatus: {
      ...context.apiStatus,
      cancelInstallmentOrderApiStatus: 'error'
    }
  };
});

export const onEnterUpdateUserProfileDetailsState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      updateUserProfileDetailsApiStatus: 'loading'
    }
  };
});

export const onSuccessUpdateUserProfileDetails = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      updateUserProfileDetailsApiStatus: 'success'
    }
  };
});

export const onErrorUpdateUserProfileDetails = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorMessage = getAPIErrorMessage(typedEvent.data);
  showErrorMessageToast(errorMessage);
  return {
    apiStatus: {
      ...context.apiStatus,
      updateUserProfileDetailsApiStatus: 'error'
    }
  };
});

export const setUserSelectedPaymentService = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as CreatePaymentOrderEvent;

  return {
    userSelectedPaymentService: typedEvent.selectedPaymentService,
    userSelectedPaymentMode: typedEvent.selectedPaymentMode
  };
});

export const setDefaultPaymentService = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const { selectedProductCode, productsDetailedInfo } = context;

  if (!selectedProductCode || !productsDetailedInfo?.[selectedProductCode]) {
    return {};
  }

  if (productsDetailedInfo[selectedProductCode].paymentServiceInfo) {
    return {
      userSelectedPaymentService:
        productsDetailedInfo[selectedProductCode].paymentServiceInfo
          .paymentServicesDetails[0].paymentService,
      userSelectedPaymentMode:
        productsDetailedInfo[selectedProductCode].paymentServiceInfo
          .paymentServicesDetails[0].paymentModes[0]
    };
  } else {
    return {};
  }
});

export const removePromotionalCodes = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as CheckReferralCodeEvent;

  const {
    errorMessages: { checkCouponCodeErrorMessage, checkInviteCodeErrorMessage },
    apiStatus: { checkCouponCodeApiStatus, checkInviteCodeApiStatus }
  } = context;

  const isInviteCode = typedEvent.category === 'invite_code';

  if (typedEvent.action === 'remove') {
    return {
      promotionalCodes: {
        ...context.promotionalCodes,
        coupon_code: isInviteCode ? context.promotionalCodes?.coupon_code : '',
        invite_code: isInviteCode ? '' : context.promotionalCodes?.invite_code
      },
      errorMessages: {
        ...context.errorMessages,
        checkCouponCodeErrorMessage: isInviteCode
          ? checkCouponCodeErrorMessage
          : '',
        checkInviteCodeErrorMessage: isInviteCode
          ? ''
          : checkInviteCodeErrorMessage
      },
      apiStatus: {
        ...context.apiStatus,
        checkCouponCodeApiStatus: isInviteCode
          ? checkCouponCodeApiStatus
          : 'idle',
        checkInviteCodeApiStatus: isInviteCode
          ? 'idle'
          : checkInviteCodeApiStatus
      }
    };
  }
  return {};
});

export const updatePromotionalCodes = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const { checkReferralCodeEvent } = context;
  if (!checkReferralCodeEvent) {
    return {};
  }
  return {
    promotionalCodes: {
      ...context.promotionalCodes,
      [checkReferralCodeEvent.category]: checkReferralCodeEvent.referralCode
    }
  };
});

export const onEnterCheckReferralCodeState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as CheckReferralCodeEvent;

  return {
    checkReferralCodeEvent: typedEvent,
    apiStatus: {
      ...context.apiStatus,
      checkCouponCodeApiStatus:
        typedEvent.category === 'coupon_code'
          ? 'loading'
          : context.apiStatus.checkCouponCodeApiStatus,
      checkInviteCodeApiStatus:
        typedEvent.category === 'invite_code'
          ? 'loading'
          : context.apiStatus.checkInviteCodeApiStatus
    }
  };
});

export const onSuccessCheckReferralCode = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as CheckReferralCodeSuccessEvent;

  const { productsDetailedInfo, selectedProductCode, checkReferralCodeEvent } =
    context;

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  if (
    !selectedProductCode ||
    !updatedProductsDetailedInfo?.[selectedProductCode] ||
    !updatedProductsDetailedInfo[selectedProductCode].productDetails
  ) {
    return {};
  }

  const updateProductDetails = createProductWithUpdatedPricingDetailModel(
    typedEvent.data,
    updatedProductsDetailedInfo[selectedProductCode].productDetails
  );

  if (updateProductDetails) {
    updatedProductsDetailedInfo[selectedProductCode].productDetails =
      updateProductDetails as AllProductTypes;
  }

  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    isCouponCodeApplied:
      checkReferralCodeEvent?.category === 'coupon_code'
        ? checkReferralCodeEvent?.action === 'apply'
          ? true
          : false
        : context.isCouponCodeApplied,
    isInviteCodeApplied:
      checkReferralCodeEvent?.category === 'invite_code'
        ? checkReferralCodeEvent?.action === 'apply'
          ? true
          : false
        : context.isInviteCodeApplied,
    apiStatus: {
      ...context.apiStatus,
      checkCouponCodeApiStatus:
        checkReferralCodeEvent?.category === 'coupon_code'
          ? 'success'
          : context.apiStatus.checkCouponCodeApiStatus,
      checkInviteCodeApiStatus:
        checkReferralCodeEvent?.category === 'invite_code'
          ? 'success'
          : context.apiStatus.checkInviteCodeApiStatus
    },
    errorMessages: {
      ...context.errorMessages,
      checkCouponCodeErrorMessage:
        checkReferralCodeEvent?.category === 'coupon_code'
          ? ''
          : context.errorMessages.checkCouponCodeErrorMessage,
      checkInviteCodeErrorMessage:
        checkReferralCodeEvent?.category === 'invite_code'
          ? ''
          : context.errorMessages.checkInviteCodeErrorMessage
    }
  };
});

export const onErrorCheckReferralCode = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const { checkReferralCodeEvent } = context;

  const typedEvent = event as ErrorExecutionEvent;

  const errorMessage = getAPIErrorResponse(typedEvent.data);

  return {
    apiStatus: {
      ...context.apiStatus,
      checkCouponCodeApiStatus:
        checkReferralCodeEvent?.category === 'coupon_code'
          ? 'error'
          : context.apiStatus.checkCouponCodeApiStatus,
      checkInviteCodeApiStatus:
        checkReferralCodeEvent?.category === 'invite_code'
          ? 'error'
          : context.apiStatus.checkInviteCodeApiStatus
    },
    errorMessages: {
      ...context.errorMessages,
      checkCouponCodeErrorMessage:
        checkReferralCodeEvent?.category === 'coupon_code'
          ? errorMessage
          : context.errorMessages.checkCouponCodeErrorMessage,
      checkInviteCodeErrorMessage:
        checkReferralCodeEvent?.category === 'invite_code'
          ? errorMessage
          : context.errorMessages.checkInviteCodeErrorMessage
    }
  };
});

export const OnClosePaymentOptionModalAction = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const { selectedProductCode, productsDetailedInfo } = context;

  if (!selectedProductCode || !productsDetailedInfo?.[selectedProductCode]) {
    return {};
  }

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  updatedProductsDetailedInfo[selectedProductCode] = {
    ...updatedProductsDetailedInfo[selectedProductCode],
    paymentServiceInfo: undefined,
    createPaymentOrderResponse: undefined,
    createInstallmentOrderResponse: undefined
  };
  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    apiStatus: {
      ...context.apiStatus,
      createPaymentOrderAndGetDetailApiStatus: 'idle',
      submitProductOrCreatePaymentApiStatus: 'idle',
      getSelectedProductPaymentServiceApiStatus: 'idle',
      createInstallmentOrderApiStatus: 'idle',
      cancelInstallmentOrderApiStatus: 'idle'
    },
    errorMessages: {
      ...context.errorMessages,
      createPaymentOrderAndGetDetailErrorMessage: '',
      getSelectedProductPaymentServiceErrorMessage: '',
      createInstallmentOrderErrorMessage: '',
      cancelInstallmentOrderErrorMessage: '',
      submitProductOrCreatePaymentErrorMessage: ''
    }
  };
});

export const onEnterCreateChangeProgramOrderState = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  return {
    apiStatus: {
      ...context.apiStatus,
      createChangeProgramOrderApiStatus: 'loading'
    }
  };
});

export const onSuccessCreateChangeProgramOrder = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as CreateChangeProgramOrderSuccessEvent;

  const { productsDetailedInfo, selectedProductCode } = context;

  if (!selectedProductCode || !productsDetailedInfo?.[selectedProductCode]) {
    return {};
  }

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  updatedProductsDetailedInfo[selectedProductCode] = {
    ...updatedProductsDetailedInfo[selectedProductCode],
    createChangeProgramOrderResponse: typedEvent.data
  };
  return {
    productsDetailedInfo: updatedProductsDetailedInfo,
    isChangedProgramProduct: true,
    apiStatus: {
      ...context.apiStatus,
      createChangeProgramOrderApiStatus: 'success'
    }
  };
});

export const onErrorCreateChangeProgramOrder = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const typedEvent = event as ErrorExecutionEvent;
  const errorResponse = getAPIErrorResponse(typedEvent.data);
  return {
    apiStatus: {
      ...context.apiStatus,
      createChangeProgramOrderApiStatus: 'error'
    },
    errorMessages: {
      ...context.errorMessages,
      createChangeProgramOrderErrorMessage: errorResponse
    }
  };
});

export const updateIsChangedProgramProduct = assign<
  PaymentsCoreMachineContext,
  PaymentCoreMachineEvents
>((context, event) => {
  const { selectedProductCode, productsDetailedInfo } = context;
  const typedEvent = event as UpdateIsChangedProgramProductEvent;

  if (!selectedProductCode || !productsDetailedInfo?.[selectedProductCode]) {
    return {};
  }

  const updatedProductsDetailedInfo = { ...productsDetailedInfo };

  updatedProductsDetailedInfo[selectedProductCode] = {
    ...updatedProductsDetailedInfo[selectedProductCode]
  };

  return {
    isChangedProgramProduct: typedEvent.isChangedProgramProduct,
    productsDetailedInfo: updatedProductsDetailedInfo
  };
});

export const getCreatePaymentOrderAndGetDetailRequestObject = (
  context: PaymentsCoreMachineContext
) => {
  const {
    userProfileDetails,
    productsDetailedInfo,
    selectedProductCode,
    promotionalCodes,
    userSelectedPaymentService,
    formValues,
    isInviteCodeApplied,
    isCouponCodeApplied
  } = context;

  if (!selectedProductCode || !productsDetailedInfo?.[selectedProductCode]) {
    return {
      amount: 0,
      paymentServiceDetail: null,
      baseRequestObject: null
    };
  }

  const product = productsDetailedInfo[selectedProductCode];

  const amount =
    product.productDetails?.currentPricingDetails.amountToBePaidInLeastSubunit;

  const acceptedTnc = product.productDetails?.tncToAccept ?? [];

  const paymentServiceDetail =
    product.paymentServiceInfo?.paymentServicesDetails.find(
      (service) => service.paymentService === userSelectedPaymentService
    );

  const {
    email,
    phone,
    fullName,
    currentState,
    currentDistrict,
    couponCode,
    inviteCode,
    ...restValues
  } = formValues;

  const changeProgramDetail = {
    isChangedProgramProduct: context.isChangedProgramProduct,
    orderId: product.createChangeProgramOrderResponse?.order_id
  };

  const baseRequestObject = {
    invite_code: isInviteCodeApplied ? promotionalCodes?.invite_code : '',
    coupon_code: isCouponCodeApplied ? promotionalCodes?.coupon_code : '',
    user_details: {
      full_name: (fullName as string) ?? userProfileDetails.name,
      email: (email as string) ?? userProfileDetails.email,
      phone_number: (phone as string) ?? userProfileDetails.phoneNumber,
      state_of_residence:
        (currentState as Record<string, string>)?.['state_name'] ??
        userProfileDetails.stateOfResidence
    },
    extra_info_based_on_product_config: JSON.stringify({
      utm_campaign: getQueryParamValue('utm_campaign') ?? '',
      utm_medium: getQueryParamValue('utm_medium') ?? '',
      utm_term: getQueryParamValue('utm_term') ?? '',
      utm_content: getQueryParamValue('utm_content') ?? '',
      utm_source: getQueryParamValue('utm_source') ?? '',
      currentState: (currentState as Record<string, string>)?.['state_name'],
      currentDistrict: (currentDistrict as Record<string, string>)?.[
        'district_name'
      ],
      ...restValues
    }),
    product_code: selectedProductCode,
    accepted_tnc: acceptedTnc,
    amount_to_be_paid_in_least_subunit: amount ?? 0
  };
  return {
    changeProgramDetail,
    amount,
    paymentServiceDetail,
    baseRequestObject
  };
};

export const isProductAmountZero = (context: PaymentsCoreMachineContext) => {
  const { productsDetailedInfo, selectedProductCode } = context;
  const product = productsDetailedInfo?.[selectedProductCode ?? ''];
  const amount = product?.productDetails?.currentPricingDetails.amountToBePaid;
  return amount === 0;
};
